/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Paddle.js V2 Overlay Checkout Loader & Client (Task 6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

declare global {
  interface Window {
    Paddle?: {
      Initialize: (options: {
        token: string;
        environment?: 'sandbox' | 'production';
        eventCallback?: (data: any) => void;
      }) => void;
      Checkout: {
        open: (options: {
          transactionId?: string;
          settings?: {
            displayMode?: 'overlay' | 'inline';
            theme?: 'light' | 'dark';
            locale?: string;
            successUrl?: string;
          };
          items?: Array<{ priceId: string; quantity: number }>;
          customer?: { email?: string };
        }) => void;
      };
      Environment: {
        set: (env: 'sandbox' | 'production') => void;
      };
    };
  }
}

let isPaddleScriptLoaded = false;
let paddleInitPromise: Promise<boolean> | null = null;

/**
 * Check if Paddle script is already loaded
 */
export function isPaddleReady(): boolean {
  return isPaddleScriptLoaded || (typeof window !== 'undefined' && !!window.Paddle);
}

/**
 * Dynamically load Paddle.js script on demand
 */
export async function loadPaddleScript(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (window.Paddle) return true;

  if (paddleInitPromise) return paddleInitPromise;

  paddleInitPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[src*="paddle.com/paddle/v2/paddle.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      isPaddleScriptLoaded = true;
      resolve(true);
    };
    script.onerror = () => {
      console.warn('[Paddle] Failed to load Paddle.js script from CDN.');
      resolve(false);
    };
    document.head.appendChild(script);
  });

  return paddleInitPromise;
}

/**
 * Initialize Paddle instance with client token
 */
export async function initializePaddle(
  clientToken: string = 'test_token_placeholder',
  environment: 'sandbox' | 'production' = 'sandbox'
): Promise<boolean> {
  const loaded = await loadPaddleScript();
  if (!loaded || !window.Paddle) return false;

  try {
    window.Paddle.Initialize({
      token: clientToken,
      environment,
    });
    return true;
  } catch (err) {
    console.warn('[Paddle] Initialization warning:', err);
    return false;
  }
}

/**
 * Open Paddle overlay checkout with transaction ID or price IDs
 */
export async function openPaddleOverlay(params: {
  transactionId?: string;
  successUrl?: string;
  customerEmail?: string;
  theme?: 'light' | 'dark';
}): Promise<boolean> {
  const isReady = await initializePaddle();
  if (!isReady || !window.Paddle) {
    return false;
  }

  try {
    window.Paddle.Checkout.open({
      transactionId: params.transactionId,
      settings: {
        displayMode: 'overlay',
        theme: params.theme || 'dark',
        successUrl: params.successUrl,
      },
      customer: {
        email: params.customerEmail,
      },
    });
    return true;
  } catch (err) {
    console.warn('[Paddle] Checkout open failed:', err);
    return false;
  }
}
